"use client";

import { c as _c } from "react/compiler-runtime";
import { trackEmailComposerAction } from "../analytics";
import { readStyle, writeStyle } from "../blocks/shared-block-styles";
import { useEmailComposer } from "../context";
import { NodeSelection } from "@tiptap/pm/state";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useCallback, useEffect, useRef, useState } from "react";
import { LuAlignCenter, LuAlignLeft, LuAlignRight } from "react-icons/lu";
import { Button, Input } from "@/components/ui";
import { isColorDark } from "@/lib/color";
import { cx } from "@/lib/tailwind-utils";
import { getClickedCellIndex, getClickedNonTextBlockIndex, getClickedNonTextBlockType } from "./node-selection-drag-plugin";
type ButtonState = {
  isActive: boolean;
  text: string;
  href: string;
  backgroundColor: string;
  textColor: string;
  alignment: string;
  borderRadius: string;
  borderWidth: string;
  borderStyle: string;
  borderColor: string;
  paddingHorizontal: string;
  paddingVertical: string;
  buttonPos: number;
};
const ALIGNMENT_OPTIONS = [{
  value: "left",
  icon: LuAlignLeft,
  label: "Left"
}, {
  value: "center",
  icon: LuAlignCenter,
  label: "Center"
}, {
  value: "right",
  icon: LuAlignRight,
  label: "Right"
}] as const;
const BORDER_RADIUS_PRESETS = [{
  value: "4px",
  label: "S"
}, {
  value: "8px",
  label: "M"
}, {
  value: "12px",
  label: "L"
}, {
  value: "9999px",
  label: "Full"
}] as const;
const BORDER_STYLE_OPTIONS = ["solid", "dashed", "dotted", "double", "groove", "ridge"] as const;

/** Map legacy preset keys to px values. */
const LEGACY_PRESETS: Record<string, string> = {
  sm: "4px",
  md: "8px",
  lg: "12px",
  full: "9999px"
};
function resolveRadius(value: string): string {
  return LEGACY_PRESETS[value] ?? value;
}
function normalizeHref(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "") return trimmed;
  if (/^(javascript|data):/i.test(trimmed)) return "";
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}
function parsePx(value: string): number {
  return parseInt(value, 10) || 0;
}
function Separator() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-px bg-border" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Small px input with label */
function PxInput(t0) {
  const $ = _c(16);
  const {
    label,
    value,
    onChange,
    placeholder,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("flex items-center gap-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = label && <span className="text-tiny text-foreground-secondary w-5 shrink-0">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== value) {
    t3 = parsePx(value);
    $[4] = value;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== onChange) {
    t4 = e => {
      const n = parseInt(e.target.value, 10);
      onChange(isNaN(n) || n < 0 ? "0px" : `${n}px`);
    };
    $[6] = onChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = placeholder ?? "0";
  let t6;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t5) {
    t6 = <Input value={t3} onChange={t4} placeholder={t5} className="h-6 w-12 text-caption text-center px-1" type="number" min={0} />;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t6) {
    t7 = <div className={t1}>{t2}{t6}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

/**
 * Find the emailButton node from the current selection.
 * Searches the clicked cell first, then falls back to all cells.
 */
function getButtonFromSelection(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
editor: any): {
  attrs: Record<string, unknown>;
  pos: number;
} | null {
  if (!editor) return null;
  const sel = editor.state.selection;
  if (!(sel instanceof NodeSelection)) return null;
  if (sel.node.type.name !== "emailRow") return null;
  const row = sel.node;
  const clickedIdx = getClickedCellIndex();

  // Search a specific cell for a button
  function searchCell(cellIdx: number, blockIndex: number): {
    attrs: Record<string, unknown>;
    pos: number;
  } | null {
    if (cellIdx >= row.childCount) return null;
    let cellStart = sel.from + 1; // inside the row
    for (let c = 0; c < cellIdx; c++) cellStart += row.child(c).nodeSize;
    const cell = row.child(cellIdx);
    let offset = cellStart + 1; // inside the cell
    let matchCount = 0;
    for (let i = 0; i < cell.childCount; i++) {
      const block = cell.child(i);
      if (block.type.name === "emailButton") {
        if (matchCount === blockIndex) {
          return {
            attrs: block.attrs,
            pos: offset
          };
        }
        matchCount++;
      }
      offset += block.nodeSize;
    }
    return null;
  }

  // Only search the clicked cell, targeting the specific block index
  const blockIndex = getClickedNonTextBlockIndex();
  const result = searchCell(clickedIdx, blockIndex);
  if (!result) return null;

  // When the button shares its cell with other blocks (an image, a heading
  // over a background), only show this menu if the user clicked the button.
  const clickedType = getClickedNonTextBlockType();
  const sharesCell = row.child(clickedIdx).childCount > 1;
  if (clickedType !== "emailButton" && (sharesCell || clickedType)) return null;
  return result;
}
export function ButtonBubbleMenu() {
  const {
    editor,
    bubbleMenuContainerRef
  } = useEmailComposer();
  const state = useEditorState({
    editor,
    selector: (ctx): ButtonState | null => {
      const result = getButtonFromSelection(ctx.editor);
      if (!result) return null;
      const {
        attrs,
        pos
      } = result;
      return {
        isActive: true,
        text: attrs.text as string ?? "Click here",
        href: attrs.href as string ?? "",
        backgroundColor: readStyle(attrs, "backgroundColor") || "#006bb5",
        textColor: readStyle(attrs, "textColor", "color") || "#ffffff",
        alignment: attrs.alignment as string ?? "center",
        borderRadius: resolveRadius(readStyle(attrs, "borderRadius") || "12px"),
        borderWidth: readStyle(attrs, "borderWidth") || "0px",
        borderStyle: readStyle(attrs, "borderStyle") || "solid",
        borderColor: readStyle(attrs, "borderColor") || "#000000",
        paddingHorizontal: readStyle(attrs, "paddingHorizontal", "padding-left") || "24px",
        paddingVertical: readStyle(attrs, "paddingVertical", "padding-top") || "8px",
        buttonPos: pos
      };
    }
  });
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local state when the menu becomes active or targets a different button
  const prevActiveRef = useRef(false);
  const prevButtonPosRef = useRef<number | null>(null);
  if (state?.isActive && (!prevActiveRef.current || state.buttonPos !== prevButtonPosRef.current)) {
    setUrl(state.href);
    setText(state.text);
  }
  prevActiveRef.current = state?.isActive ?? false;
  prevButtonPosRef.current = state?.buttonPos ?? null;
  const updateAttr = useCallback((attrs_0: Record<string, unknown>) => {
    if (!editor || !state) return;
    const buttonNode = editor.state.doc.nodeAt(state.buttonPos);
    if (!buttonNode || buttonNode.type.name !== "emailButton") return;
    const sel = editor.state.selection;
    const {
      tr
    } = editor.state;
    tr.setNodeMarkup(state.buttonPos, undefined, {
      ...buttonNode.attrs,
      ...attrs_0
    });
    // Re-assert NodeSelection on the row so the bubble menu stays open
    if (sel instanceof NodeSelection) {
      tr.setSelection(NodeSelection.create(tr.doc, sel.from));
    }
    editor.view.dispatch(tr);
  }, [editor, state]);

  /**
   * Edit the button's style-shaped attrs.
   *
   * A button parsed out of a designed template keeps that template's whole
   * `style` attribute; writing an attr also drops the map's copy of the same
   * declaration, so the edit is what renders rather than being painted back
   * over by the source.
   */
  const updateStyleAttrs = useCallback((writes: {
    property: string;
    value: string;
    cssProperty?: string;
  }[]) => {
    if (!editor || !state) return;
    const buttonNode_0 = editor.state.doc.nodeAt(state.buttonPos);
    if (!buttonNode_0) return;
    let attrs_1: Record<string, unknown> = buttonNode_0.attrs;
    for (const {
      property,
      value,
      cssProperty
    } of writes) {
      attrs_1 = {
        ...attrs_1,
        ...writeStyle(attrs_1, property, value, {
          target: "named",
          cssProperty
        })
      };
    }
    updateAttr(attrs_1);
  }, [editor, state, updateAttr]);
  const updateStyleAttr = useCallback((property_0: string, value_0: string, cssProperty_0?: string) => updateStyleAttrs([{
    property: property_0,
    value: value_0,
    cssProperty: cssProperty_0
  }]), [updateStyleAttrs]);
  const applyLink = useCallback(() => {
    if (!editor) return;
    const href = normalizeHref(url);
    updateAttr({
      href
    });
    trackEmailComposerAction("button_link_applied", "bubble_menu");
  }, [editor, url, updateAttr]);
  const applyText = useCallback(() => {
    if (!editor) return;
    updateAttr({
      text: text || "Click here"
    });
    trackEmailComposerAction("button_text_changed", "bubble_menu");
  }, [editor, text, updateAttr]);

  // Escape key deselects
  useEffect(() => {
    if (!editor || !state?.isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Only if the focused element is not an input inside the bubble menu
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLSelectElement) {
          (active as HTMLElement).blur();
          return;
        }
        e.preventDefault();
        editor.commands.blur();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [editor, state?.isActive]);
  if (!editor || !state?.isActive) return null;
  return <BubbleMenu editor={editor} shouldShow={() => getButtonFromSelection(editor) !== null} getReferencedVirtualElement={() => {
    const result_0 = getButtonFromSelection(editor);
    if (!result_0) return null;
    const sel_0 = editor.state.selection;
    if (!(sel_0 instanceof NodeSelection)) return null;
    // Get the row DOM (vanilla JS NodeView — always reliable), then
    // find the clicked cell and the button element inside it.
    const rowDom = editor.view.nodeDOM(sel_0.from);
    if (!(rowDom instanceof HTMLElement)) return null;
    const clickedIdx = getClickedCellIndex();
    const cellDom = rowDom.children[clickedIdx] ?? rowDom.children[0];
    if (!(cellDom instanceof HTMLElement)) return null;
    const btns = cellDom.querySelectorAll('[data-block-type="emailButton"]');
    const btnIdx = getClickedNonTextBlockIndex();
    const btn = btns[btnIdx] ?? btns[0];
    if (!btn) return null;
    return {
      getBoundingClientRect: () => btn.getBoundingClientRect(),
      getClientRects: () => [btn.getBoundingClientRect()],
      contextElement: btn
    };
  }} options={{
    autoPlacement: {
      allowedPlacements: ["top", "bottom"]
    }
  }} appendTo={bubbleMenuContainerRef.current ?? undefined}>
      <div className="rounded-lg border border-border bg-background shadow-md w-[280px]">
        {/* Button Text */}
        <div className="px-2 py-1.5">
          <form onSubmit={e_0 => {
          e_0.preventDefault();
          e_0.stopPropagation();
          applyText();
        }} className="flex items-center gap-1.5">
            <span className="text-tiny text-foreground-secondary shrink-0">
              Text
            </span>
            <Input value={text} onChange={e_1 => {
            const newText = e_1.target.value;
            setText(newText);
            updateAttr({
              text: newText || "Click here"
            });
          }} placeholder="Click here" className="h-7 text-caption" />
          </form>
        </div>

        <Separator />

        {/* URL */}
        <div className="px-2 py-1.5">
          <form onSubmit={e_2 => {
          e_2.preventDefault();
          e_2.stopPropagation();
          applyLink();
        }} className="flex items-center gap-1.5">
            <span className="text-tiny text-foreground-secondary shrink-0">
              URL
            </span>
            <Input ref={inputRef} value={url} onChange={e_3 => setUrl(e_3.target.value)} onBlur={applyLink} placeholder="https://..." className="h-7 text-caption" />
          </form>
        </div>

        <Separator />

        {/* Colors + Alignment */}
        <div className="px-2 py-1.5 flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-tiny text-foreground-secondary">BG</span>
            <input type="color" value={state.backgroundColor} onChange={e_4 => {
            const bg = e_4.target.value;
            updateStyleAttrs([{
              property: "backgroundColor",
              value: bg
            }, {
              property: "textColor",
              value: isColorDark(bg) ? "#ffffff" : "#000000",
              cssProperty: "color"
            }]);
          }} className="h-6 w-6 cursor-pointer rounded border border-border" title="Background color" />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-tiny text-foreground-secondary">Text</span>
            <input type="color" value={state.textColor} onChange={e_5 => updateStyleAttr("textColor", e_5.target.value, "color")} className="h-6 w-6 cursor-pointer rounded border border-border" title="Text color" />
          </div>

          <div className="ml-auto flex items-center gap-0.5">
            {ALIGNMENT_OPTIONS.map(({
            value: value_1,
            icon: Icon,
            label
          }) => <Button type="button" key={value_1} size="icon-sm" variant="ghost" className={cx("h-6 w-6", state.alignment === value_1 && "bg-hover")} onMouseDown={e_6 => e_6.preventDefault()} onClick={() => {
            updateAttr({
              alignment: value_1
            });
            trackEmailComposerAction("button_alignment_changed", "bubble_menu", {
              alignment: value_1
            });
          }} tooltip={label}>
                <Icon size={14} />
              </Button>)}
          </div>
        </div>

        <Separator />

        {/* Border Radius */}
        <div className="px-2 py-1.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-tiny text-foreground-secondary shrink-0">
              Radius
            </span>
            <PxInput value={state.borderRadius} onChange={v => updateStyleAttr("borderRadius", v)} placeholder="12" />
            <div className="flex items-center gap-0.5 ml-auto">
              {BORDER_RADIUS_PRESETS.map(({
              value: value_2,
              label: label_0
            }) => <Button type="button" key={value_2} size="sm" variant="ghost" className={cx("h-6 px-1.5 text-caption", state.borderRadius === value_2 ? "bg-hover text-foreground font-semibold" : "text-foreground-secondary")} onMouseDown={e_7 => e_7.preventDefault()} onClick={() => {
              updateStyleAttr("borderRadius", value_2);
              trackEmailComposerAction("button_radius_changed", "bubble_menu", {
                borderRadius: value_2
              });
            }}>
                  {label_0}
                </Button>)}
            </div>
          </div>
        </div>

        <Separator />

        {/* Padding */}
        <div className="px-2 py-1.5 flex items-center gap-1.5">
          <PxInput label="H" value={state.paddingHorizontal} onChange={v_0 => updateStyleAttr("paddingHorizontal", v_0, "padding-left")} placeholder="24" />
          <PxInput label="W" value={state.paddingVertical} onChange={v_1 => updateStyleAttr("paddingVertical", v_1, "padding-top")} placeholder="8" />
        </div>

        <Separator />

        {/* Border */}
        <div className="px-2 py-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-tiny text-foreground-secondary shrink-0">
              Border
            </span>
            <PxInput value={state.borderWidth} onChange={v_2 => updateStyleAttr("borderWidth", v_2)} placeholder="0" />
            <select value={state.borderStyle} onChange={e_8 => updateStyleAttr("borderStyle", e_8.target.value)} className="h-6 text-caption rounded border border-border bg-background px-1 text-foreground">
              {BORDER_STYLE_OPTIONS.map(s => <option key={s} value={s}>
                  {s}
                </option>)}
            </select>
            <input type="color" value={state.borderColor} onChange={e_9 => updateStyleAttr("borderColor", e_9.target.value)} className="h-6 w-6 cursor-pointer rounded border border-border" title="Border color" />
          </div>
        </div>
      </div>
    </BubbleMenu>;
}